import { message as antMessage } from "antd";
import { ArrowDown } from "lucide-react";
import useAuthStore from "../../store/authStore";
import MessageList from "./MessageList";
import { useRef, useState, useEffect, useCallback } from "react";
import useStoredUser from "../../hooks/useStoredUser";
import { fetchCareStatuses, mapCareStatusToLabel } from "./chatLabels"
import LabelBar from "./LabelBar";
import Composer from "./Composer";
import ChatHeader from "./ChatHeader";
import type { ChatMessage, OutgoingDraft } from "./types";
import { parsePayload, extractImages, extractAudios, extractVideos, extractStoryMention, extractAdsFromAttachments, buildAdCards } from "./chatPayload";
import { getChannelMessages, sendChannelMessage, sendInstagramMessage, ChannelMessageItem, getMessageSenderName, cleanStaffName } from "../../api/ChannelMessage";
import { getChannelComments, replyChannelComment, ChannelCommentItem } from "../../api/ChannelComment";
import { isRealtimeEnabled, subscribeChannelMessages, COL_CUSTOMER_ID } from "../../services/realtime";
import { REALTIME_FALLBACK_POLL } from "env";
import { updateChannelCustomer, restoreUnread, updateChannelCustomerAvatar, getChannelCustomerDetail } from "../../api/ChannelCustomer";
import { uploadPhotoLibrary } from "../../api/PhotoLibrary";
import { useFbFinder } from "../../hooks/useFbFinder";
import {
    buildCommentPermalink,
    buildInboxUrl,
    buildPeopleSearchUrl,
    isFacebookChannel,
} from "../../utils/facebook";
import { useTranslation } from "react-i18next";

/** Cách đáy khung chat dưới mức này (px) thì coi như đang xem tin mới nhất */
const NEAR_BOTTOM_PX = 120;

export default function ChatWindow({ user, activeLabels, toggleLabel, goBack, openInfo, connectionExternalId, isGroup }: any) {
    const { t, i18n } = useTranslation();
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const isReadOnly = !isSuperAdmin && !hasPermission("chats.reply");
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [careLabels, setCareLabels] = useState<any[]>([]);
    const storedUser = useStoredUser();
    // localStorage "user" có 2 dạng: đăng nhập mới lưu thẳng bản ghi user (fullname/name, avatar, id),
    // phiên cũ lưu dạng { user_id, fullname, info: {...} } -> đọc được cả hai
    const myAvatar = storedUser.avatar || storedUser.avatar_url || storedUser.info?.avatar || '';
    // Tài khoản đang đăng nhập = người gửi tin: hiện ở ô soạn tin và gắn cho tin đang gửi
    const myUserId: number | null = Number(storedUser.user_id ?? storedUser.id ?? storedUser.info?.id) || null;
    const myName: string = cleanStaffName(
        storedUser.fullname || storedUser.name || storedUser.info?.fullname || storedUser.info?.name
    );
    const [isPinned, setIsPinned] = useState(false);
    const [pinning, setPinning] = useState(false);
    const [restoringUnread, setRestoringUnread] = useState(false);

    useEffect(() => {
        setIsPinned(!!user?.isPinned);
    }, [user?.id, user?.isPinned]);

    const handleTogglePin = useCallback(async () => {
        if (!user?.id || pinning) return;
        const next = !isPinned;
        setPinning(true);
        setIsPinned(next);
        const res = await updateChannelCustomer(user.id, { is_ping: next ? 1 : 0 });
        if (res.status) {
            antMessage.success(next ? t("roomchat.pin_success") : t("roomchat.unpin_success"));
        } else {
            setIsPinned(!next);
            antMessage.error(t("roomchat.pin_error"));
        }
        setPinning(false);
    }, [user?.id, isPinned, pinning, t]);

    // Khôi phục số tin nhắn chưa đọc (lỡ đánh dấu đã đọc)
    const handleRestoreUnread = useCallback(async () => {
        if (!user?.id || restoringUnread) return;
        setRestoringUnread(true);
        const res = await restoreUnread(user.id);
        if (res.status) {
            antMessage.success(t("roomchat.restore_unread_success") || "Đã khôi phục tin chưa đọc");
            // Báo danh sách hội thoại làm mới badge unread
            window.dispatchEvent(new Event("conversation-unread-restored"));
        } else {
            antMessage.error(res.msg || t("roomchat.restore_unread_error") || "Khôi phục thất bại");
        }
        setRestoringUnread(false);
    }, [user?.id, restoringUnread, t]);

    useEffect(() => {
        fetchCareStatuses().then((items) => {
            setCareLabels(items.map(mapCareStatusToLabel));
        });
    }, []);

    const formatTime = (isoString: string) => {
        if (!isoString) return "";
        try {
            const date = new Date(isoString);
            return date.toLocaleString(i18n.language === 'vi' ? 'vi-VN' : 'en-US', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return isoString;
        }
    };

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const prevMessageCountRef = useRef(0);
    /** Khung cuộn danh sách tin */
    const listRef = useRef<HTMLDivElement>(null);
    // Đang xem gần cuối khung chat -> tin mới đến thì tự cuộn theo; đang kéo lên đọc tin cũ thì giữ nguyên chỗ
    const nearBottomRef = useRef(true);
    // Vừa tải xong lịch sử của khách (lần tải đầu, không phải làm mới) -> nhảy thẳng xuống cuối
    const jumpToEndRef = useRef(false);
    // Số tin mới đến trong lúc đang đọc tin cũ (hiện nút "Tin mới ↓")
    const [unseenCount, setUnseenCount] = useState(0);

    const mapMessages = useCallback((items: ChannelMessageItem[]) => {
        return items.map((msg: ChannelMessageItem) => {
            // Payload cũ là chuỗi JSON: parse 1 lần rồi dùng chung cho các hàm extract
            // (parsePayload gặp object thì trả nguyên, không parse lại)
            const parsed = { ...msg, payload: parsePayload(msg.payload) };
            const adAttachments = extractAdsFromAttachments(parsed);
            return {
                id: msg.id,
                text: msg.text || "",
                sender: msg.direction === "inbound" ? user.name : t("roomchat.you"),
                time: formatTime(msg.sent_at),
                rawTime: msg.sent_at,
                adInfo: msg.ad_info ?? null,
                adJson: msg.ad_json ?? null,
                adAttachments,
                ads: buildAdCards(msg.ad_info, msg.ad_json, adAttachments),
                storyUrl: extractStoryMention(parsed),
                audios: extractAudios(parsed),
                videos: extractVideos(parsed),
                isMe: msg.direction === "outbound",
                createdById: msg.created_by_id ?? null,
                createdByName: getMessageSenderName(msg),
                images: extractImages(parsed),
                messageType: msg.message_type,
                payload: msg.payload,
                pending: false,
                failed: false,
            };
        });
    }, [user?.name]);

    // Khách loại "comment": load bình luận thay vì tin nhắn chat
    const isComment = user?.type === "comment";
    // external_comment_id của bình luận khách (inbound) mới nhất -> dùng làm parent khi Admin trả lời
    const replyParentRef = useRef<string | null>(null);

    // Khi mở cửa sổ chat: gọi API chi tiết để xác định loại kênh gửi tin.
    //  - connection.channel_type_code insta/instagram -> Instagram: dùng API sendInstagramMessage
    //  - còn lại (Facebook, Zalo OA...)        -> dùng chung API channel-message
    //    (Zalo trước đây đi qua zalo-send-message và chỉ gửi được văn bản)
    const [isInstagram, setIsInstagram] = useState(false);
    // Link trang cá nhân của khách trên kênh: ưu tiên dữ liệu từ danh sách,
    // nếu chưa có thì lấy thêm từ API chi tiết bên dưới.
    const [detailProfileLink, setDetailProfileLink] = useState<string | null>(null);
    const [channelTypeCode, setChannelTypeCode] = useState<string>("");
    // Ten kenh khach dang chat (dung khi xem theo nhom kenh de biet tra loi qua kenh nao)
    const [detailConnectionName, setDetailConnectionName] = useState<string>("");
    useEffect(() => {
        setIsInstagram(false);
        setDetailProfileLink(null);
        setChannelTypeCode("");
        setDetailConnectionName("");
        if (!user?.id) return;
        let cancelled = false;
        getChannelCustomerDetail(user.id).then((res) => {
            if (cancelled) return;
            const d = res.data as any;
            if (!res.status || !d) return;
            setDetailProfileLink(d.profile_link || null);
            const typeCode = String(d.connection?.channel_type_code || '').toLowerCase();
            setChannelTypeCode(typeCode);
            setDetailConnectionName(d.connection?.display_name || "");
            if (typeCode === 'insta' || typeCode === 'instagram') {
                setIsInstagram(true);
            }
        });
        return () => { cancelled = true; };
    }, [user?.id]);

    // Tên kênh của khách: ưu tiên dữ liệu danh sách (có sẵn ngay), thiếu thì lấy từ API chi tiết
    const replyChannelName = user?.connectionName || detailConnectionName || "";

    // Link trang cá nhân để mở tab mới khi bấm vào tên khách ở header.
    // Chuẩn hoá: bỏ khoảng trắng, tự thêm https:// nếu API chỉ trả về domain.
    const profileLink = (() => {
        const raw = String(user?.profileLink || user?.profile_link || detailProfileLink || "").trim();
        if (!raw) return "";
        return /^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, "")}`;
    })();

    // --- Tìm link Facebook thật qua extension "Khothi FB Finder" -------------
    // API không có quyền đổi PSID -> UID thật, nên phần này dựa vào phiên đăng nhập
    // Facebook của chính nhân viên: CMS chỉ đưa họ tới đúng bình luận/hội thoại,
    // extension bắt UID trên trang cá nhân họ mở và ghi ngược về CMS.
    const isFacebook = isFacebookChannel(channelTypeCode);
    const { installed: finderInstalled, findingId, startFind } = useFbFinder((u) => {
        if (u.customerId !== user?.id) return;
        if (u.profileLink) setDetailProfileLink(u.profileLink);
        if (!u.avatarUploaded) return;

        // Extension vừa upload ảnh mới -> lấy lại URL ảnh từ API và phát sự kiện chung
        // để cả header lẫn danh sách hội thoại đổi ảnh ngay, khỏi bắt nhân viên F5.
        // Extension ghi thẳng lên server (không qua API ở đây) nên phải bỏ qua kết quả đã lưu
        getChannelCustomerDetail(u.customerId, { fresh: true }).then((res) => {
            const next = res.status && res.data ? (res.data as any) : null;
            const url = next ? next.avatar || next.avatar_url || "" : "";
            if (!url) return;
            setAvatar(url);
            window.dispatchEvent(
                new CustomEvent("channel-customer-avatar-updated", {
                    detail: { id: u.customerId, avatar: url },
                })
            );
        });
    }, (r) => {
        // Kết thúc một lượt dò. Thất bại thì extension đã tự đưa tab Facebook lên trước
        // mặt nhân viên, ở đây chỉ nói rõ vì sao.
        if (r.ok) {
            antMessage.success(t("roomchat.fb_find_done"));
            return;
        }
        antMessage.warning(
            r.reason === "NEED_PICK"
                ? t("roomchat.fb_find_need_pick")
                : r.reason === "TIMEOUT"
                    ? t("roomchat.fb_find_timeout")
                    : r.reason === "EXT_STALE"
                        ? t("roomchat.fb_find_stale")
                        : t("roomchat.fb_find_failed")
        );
    });

    const fbFindTarget = (() => {
        if (!isFacebook) return "";
        if (isComment) {
            const firstInbound = messages.find((m) => !m.isMe && m.payload);
            const permalink = firstInbound ? buildCommentPermalink(firstInbound.payload) : "";
            if (permalink) return permalink;
        }
        // Khách nhắn tin: vào hộp thư của page rồi để extension dò theo tên + tin cuối.
        return buildInboxUrl(connectionExternalId) || buildPeopleSearchUrl(user?.name);
    })();

    const mapComments = useCallback((items: ChannelCommentItem[]) => {
        const lastInbound = [...items].reverse().find((c) => c.direction === "inbound");
        replyParentRef.current = lastInbound?.external_comment_id || null;
        return items.map((c: ChannelCommentItem) => ({
            id: c.id,
            text: c.text || "",
            sender: c.direction === "inbound" ? user.name : t("roomchat.you"),
            time: formatTime(c.sent_at),
            rawTime: c.sent_at,
            adInfo: null,
            adJson: null,
            isMe: c.direction === "outbound",
            createdById: c.created_by_id ?? null,
            createdByName: getMessageSenderName(c),
            images: [] as any[],
            messageType: "text",
            payload: c.payload,
            pending: false,
            failed: false,
        }));
    }, [user?.name]);

    const fetchMessages = useCallback(async (isPolling = false) => {
        if (!user?.id) return;
        if (!isPolling) setLoadingMessages(true);
        // Khách loại comment -> lấy bình luận; còn lại -> tin nhắn chat
        const fetched = isComment
            ? await getChannelComments({ customer_id: user.id, sort: ["sent_at,asc"] }).then(
                r => (r.status && r.data ? mapComments(r.data.items) : null))
            // Lần tải đầu dùng chung request với khung thông tin khách; làm mới (realtime / polling) thì lấy mới
            : await getChannelMessages({ customer_id: user.id }, { fresh: isPolling }).then(
                r => (r.status && r.data ? mapMessages(r.data.items) : null));
        if (fetched) {
            if (isPolling) {
                // Merge: keep pending/failed optimistic messages, replace confirmed ones
                setMessages(prev => {
                    const optimistic = prev.filter(m => m.pending || m.failed);
                    return [...fetched, ...optimistic];
                });
            } else {
                jumpToEndRef.current = true;
                setMessages(fetched);
            }
        }
        if (!isPolling) setLoadingMessages(false);
    }, [user?.id, isComment, mapMessages, mapComments]);

    // Initial fetch
    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);

    // Cập nhật tin nhắn mới:
    // - Có Supabase Realtime: nghe thay đổi channel_messages của khách đang mở
    //   -> refetch. Kèm polling chậm 20s làm dự phòng khi websocket rớt.
    // - Chưa cấu hình Realtime: fallback polling 3s như cũ.
    useEffect(() => {
        if (!user?.id) return;

        if (isRealtimeEnabled()) {
            const unsub = subscribeChannelMessages({
                channelName: `room-chat-messages:${user.id}`,
                filter: `${COL_CUSTOMER_ID}=eq.${user.id}`,
                onEvent: () => fetchMessages(true),
            });
            const interval = REALTIME_FALLBACK_POLL
                ? setInterval(() => fetchMessages(true), 20000)
                : null;
            return () => {
                unsub();
                if (interval) clearInterval(interval);
            };
        }

        const interval = setInterval(() => fetchMessages(true), 3000);
        return () => clearInterval(interval);
    }, [user?.id, fetchMessages]);

    const scrollToBottom = useCallback((smooth = true) => {
        const el = listRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
        nearBottomRef.current = true;
        setUnseenCount(0);
    }, []);

    const handleListScroll = () => {
        const el = listRef.current;
        if (!el) return;
        nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
        if (nearBottomRef.current) setUnseenCount(0);
    };

    // Đổi khách: bỏ đếm tin mới của hội thoại trước
    useEffect(() => {
        nearBottomRef.current = true;
        setUnseenCount(0);
    }, [user?.id]);

    // Tin mới đến: chỉ tự cuộn khi đang ở gần cuối hoặc chính mình vừa gửi;
    // đang kéo lên đọc tin cũ thì giữ nguyên chỗ và đếm số tin mới
    useEffect(() => {
        if (loadingMessages) return;
        const prev = prevMessageCountRef.current;
        prevMessageCountRef.current = messages.length;
        if (jumpToEndRef.current) {
            jumpToEndRef.current = false;
            scrollToBottom(false);
            return;
        }
        const added = messages.length - prev;
        if (added <= 0) return;
        const last = messages[messages.length - 1];
        if (nearBottomRef.current || (last?.isMe && last.pending)) {
            scrollToBottom(true);
            return;
        }
        setUnseenCount(c => c + added);
    }, [messages, loadingMessages, scrollToBottom]);

    // Có tin nhắn ảnh mới -> báo cho danh sách "Hình ảnh khách gửi" nạp lại ảnh mới
    const prevImageMsgCountRef = useRef(0);
    useEffect(() => {
        const imageMsgCount = messages.reduce(
            (n, m) => n + ((m.images?.length || 0) > 0 ? 1 : 0),
            0
        );
        if (prevImageMsgCountRef.current !== 0 && imageMsgCount > prevImageMsgCountRef.current) {
            window.dispatchEvent(
                new CustomEvent("customer-photos-updated", { detail: user?.id })
            );
        }
        prevImageMsgCountRef.current = imageMsgCount;
    }, [messages, user?.id]);

    /**
     * Gửi một tin tạm (đã hiện sẵn trong khung chat) lên server. `draft` ghi lại phần nào đã đi:
     * lỗi giữa chừng thì tin chuyển sang "Gửi thất bại", bấm Gửi lại chỉ gửi tiếp phần còn thiếu
     * (vd chữ đã đi, ảnh lỗi -> chỉ gửi lại ảnh, không gửi trùng chữ).
     */
    const deliverMessage = async (tempId: string, draft: OutgoingDraft) => {
        if (!user?.id) return;
        const progress: OutgoingDraft = { ...draft };
        const markFailed = () => setMessages(prev => prev.map(m =>
            m.id === tempId
                // Chữ đã gửi xong thì bỏ khỏi tin lỗi, chỉ còn phần ảnh chưa đi
                ? { ...m, text: progress.textSent ? "" : m.text, pending: false, failed: true, draft: progress }
                : m
        ));
        const succeed = () => {
            setMessages(prev => prev.filter(m => m.id !== tempId));
            fetchMessages(true);
        };

        // Khách loại comment: trả lời bình luận (chỉ văn bản) qua API channel-comment
        if (progress.parentCommentId) {
            const r = await replyChannelComment({ customer_id: user.id, parent_comment_id: progress.parentCommentId, text: progress.text });
            if (r.status) {
                succeed();
            } else {
                markFailed();
                antMessage.error(r.msg || t("roomchat.comment_reply_error") || "Trả lời bình luận thất bại");
            }
            return;
        }

        try {
            // 1) Lấy URL ảnh: file cục bộ -> upload thư viện ảnh; ảnh từ thư viện đã có URL.
            //    Ảnh nào đã upload ở lần trước thì giữ URL, không upload lại.
            const imageUrls = progress.imageUrls || [];
            progress.imageUrls = imageUrls;
            for (let i = imageUrls.length; i < progress.files.length; i++) {
                const f = progress.files[i];
                if (f?.file instanceof File) {
                    const up = await uploadPhotoLibrary({ image: f.file, name: f.file.name || `chat_${Date.now()}` });
                    const url = up?.data?.image;
                    if (!up.status || !url) throw new Error("upload failed");
                    imageUrls.push(url);
                } else {
                    imageUrls.push(typeof f?.preview === "string" && /^https?:\/\//.test(f.preview) ? f.preview : "");
                }
            }

            // 2) Gửi tin nhắn văn bản (nếu có, và chưa gửi ở lần trước)
            if (progress.text && !progress.textSent) {
                if (isInstagram) {
                    // Kênh Instagram: gửi qua API instagram-message
                    const r = await sendInstagramMessage({ customer_id: user.id, text: progress.text, message_type: "text" });
                    if (!r.status) throw new Error(r.msg || "send instagram failed");
                } else {
                    const r = await sendChannelMessage({ customer_id: user.id, text: progress.text, message_type: "text" });
                    if (!r.status) throw new Error("send text failed");
                }
                progress.textSent = true;
            }

            // 3) Gửi từng ảnh dưới dạng message_type "image" với payload.url, bỏ qua ảnh đã gửi
            for (let i = progress.imagesSent || 0; i < imageUrls.length; i++) {
                const url = imageUrls[i];
                if (url) {
                    const r = isInstagram
                        ? await sendInstagramMessage({ customer_id: user.id, message_type: "image", payload: { url } })
                        : await sendChannelMessage({ customer_id: user.id, message_type: "image", payload: { url } });
                    if (!r.status) throw new Error("send image failed");
                }
                progress.imagesSent = i + 1;
            }

            // Thành công: bỏ tin tạm, nạp lại từ server
            succeed();
        } catch (e) {
            markFailed();
        }
    };

    /**
     * Ô soạn tin bấm gửi: hiện ngay tin tạm trong khung chat rồi gửi lên server.
     * Trả false khi chưa gửi được (vd bình luận chưa có bình luận gốc) để ô soạn tin giữ nguyên nội dung.
     */
    const handleSend = (text: string, files: any[]): boolean => {
        if (!user?.id) return false;

        let draft: OutgoingDraft = { text, files };
        if (isComment) {
            if (!text) return false;
            const parentId = replyParentRef.current;
            if (!parentId) {
                antMessage.error(t("roomchat.comment_no_parent") || "Chưa có bình luận để trả lời");
                return false;
            }
            draft = { text, files: [], parentCommentId: parentId };
        }

        const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const now = new Date().toISOString();

        // Optimistic: hiện tin ngay, ô soạn tin tự xoá nội dung khi hàm trả true
        setMessages(prev => [...prev, {
            id: tempId,
            text,
            images: draft.files,
            sender: t("roomchat.you"),
            time: formatTime(now),
            rawTime: now,
            isMe: true,
            createdById: myUserId,
            createdByName: myName,
            pending: true,
            failed: false,
            draft,
        }]);

        deliverMessage(tempId, draft);
        return true;
    };

    /** Bấm "Gửi lại" ở tin lỗi: gửi tiếp phần chưa đi được */
    const retryMessage = (tempId: number | string) => {
        const target = messages.find(m => m.id === tempId);
        if (!target?.draft || target.pending) return;
        setMessages(prev => prev.map(m => m.id === tempId ? { ...m, pending: true, failed: false } : m));
        deliverMessage(String(tempId), target.draft);
    };

    /** Bỏ tin gửi lỗi: tin chưa lên server nên chỉ xoá khỏi khung chat */
    const deleteFailedMessage = (tempId: number | string) => {
        setMessages(prev => prev.filter(m => m.id !== tempId));
    };

    const [avatar, setAvatar] = useState(user.avatar);
    // Đồng bộ avatar theo khách đang mở (tránh giữ avatar của khách cũ khi đổi hội thoại)
    useEffect(() => { setAvatar(user?.avatar || ""); }, [user?.id, user?.avatar]);

    // Không dựng avatar từ UID: graph.facebook.com/{uid}/picture khi không có token chỉ
    // trả hình bóng người mặc định, hiển thị ra còn khó nhận ra khách hơn là chữ cái đầu.
    // Ảnh fbcdn đang lưu vẫn hết hạn sau 4-30 ngày; muốn vá triệt để phải proxy ảnh
    // phía server bằng page access token.
    const avatarSrc = avatar || undefined;

    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const handleChangeAvatar = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // cho phép chọn lại cùng file
        if (!file || !user?.id) return;

        const prev = avatar;
        const localPreview = URL.createObjectURL(file);
        setAvatar(localPreview); // hiển thị tạm ngay
        setUploadingAvatar(true);
        const res = await updateChannelCustomerAvatar(user.id, file);
        if (res.status) {
            // API cập nhật trả về đường dẫn avatar không đúng -> gọi API chi tiết lấy avatar chuẩn
            const detail = await getChannelCustomerDetail(user.id);
            const newUrl = (detail.status && detail.data)
                ? ((detail.data as any).avatar || (detail.data as any).avatar_url || localPreview)
                : localPreview;
            setAvatar(newUrl);
            antMessage.success(t("roomchat.update_success") || "Cập nhật ảnh đại diện thành công");
            // đồng bộ avatar ở danh sách hội thoại
            window.dispatchEvent(new CustomEvent("channel-customer-avatar-updated", { detail: { id: user.id, avatar: newUrl } }));
        } else {
            setAvatar(prev); // lỗi -> trả về ảnh cũ
            antMessage.error(res.msg || t("roomchat.update_failed") || "Cập nhật ảnh thất bại");
        }
        setUploadingAvatar(false);
    };

    // Nút Gửi lại / Xoá của tin lỗi: danh sách tin bên dưới được memo, gọi qua ref để luôn dùng hàm mới nhất
    const failedActionsRef = useRef({ retry: retryMessage, remove: deleteFailedMessage });
    failedActionsRef.current = { retry: retryMessage, remove: deleteFailedMessage };

    // Hàm ổn định (không đổi giữa các lần render) để MessageList được memo không render lại vô ích
    const handleRetryFailed = useCallback((id: number | string) => failedActionsRef.current.retry(id), []);
    const handleDeleteFailed = useCallback((id: number | string) => failedActionsRef.current.remove(id), []);

    return (
        <div className="flex flex-col h-full w-full">

            {/* HEADER */}
            <ChatHeader
                user={user}
                isReadOnly={isReadOnly}
                avatarSrc={avatarSrc}
                uploadingAvatar={uploadingAvatar}
                onChangeAvatar={handleChangeAvatar}
                profileLink={profileLink}
                goBack={goBack}
                openInfo={openInfo}
                fbFinder={isFacebook && finderInstalled ? { targetUrl: fbFindTarget, finding: findingId === user.id, start: startFind } : null}
                onRestoreUnread={handleRestoreUnread}
                restoringUnread={restoringUnread}
            />

            {/* MESSAGE LIST */}
            <div className="relative flex-1 min-h-0 flex flex-col">
                <div
                    ref={listRef}
                    onScroll={handleListScroll}
                    className="flex-1 overflow-y-auto p-4 bg-[#f0f2f5] dark:bg-[#181818]"
                >
                    <MessageList
                        messages={messages}
                        loading={loadingMessages}
                        myAvatar={myAvatar}
                        customerAvatar={user.avatar}
                        onRetry={handleRetryFailed}
                        onDelete={handleDeleteFailed}
                    />
                    <div ref={messagesEndRef} />
                </div>

                {/* Có tin mới trong lúc đang đọc tin cũ: không tự kéo xuống, chỉ báo */}
                {unseenCount > 0 && (
                    <button
                        type="button"
                        onClick={() => scrollToBottom(true)}
                        className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#3e91ab] text-white text-[12px] font-medium shadow-md hover:bg-[#357f96] transition-colors"
                    >
                        {t("roomchat.new_messages", { count: unseenCount })}
                        <ArrowDown size={14} />
                    </button>
                )}
            </div>

            {isReadOnly && (
                <div className="border-t border-color-1f1f1f px-4 py-2 bg-white dark:bg-[#1f1f1f]">
                    <LabelBar
                        labels={careLabels}
                        activeLabels={activeLabels ?? user.labels ?? []}
                        onToggle={() => { }}
                        readOnly
                    />
                </div>
            )}

            {/* INPUT */}
            {!isReadOnly && (
                <Composer
                    isComment={isComment}
                    careLabels={careLabels}
                    activeLabels={activeLabels ?? user.labels ?? []}
                    onToggleLabel={(key: string, id?: number) => toggleLabel(user.id, key, id)}
                    isGroup={isGroup}
                    replyChannelName={replyChannelName}
                    channelTypeCode={channelTypeCode}
                    myName={myName}
                    myAvatar={myAvatar}
                    onSend={handleSend}
                />
            )}

        </div>
    );
}